import { CREDIT_REFUNDABLE_PAYMENT_TYPES } from '@luxuryescapes/contract-svc-payment';

import { LOYALTY_POINTS_PAYMENT_TYPE, STRIPE_PAYMENT_ELEMENT_CARD_PAYMENT_TYPE } from '~/consts/payment';

import { OrderPaymentsInfo } from '../../types';

const creditRefundablePaymentTypes = new Set<string>([
  ...CREDIT_REFUNDABLE_PAYMENT_TYPES,
  // Preserve Admin's Stripe Payment Element alias alongside the backend payment types.
  STRIPE_PAYMENT_ELEMENT_CARD_PAYMENT_TYPE,
]);

/**
 * Original payments, not the remaining refundable balance.
 * - `full`: confirmed points-only payments with no outstanding payments or uncertain balance.
 * - `partial`: confirmed points and credit-refundable payments.
 * - `none`: no confirmed points, or a combination whose refund presentation is not established.
 */
export type LoyaltyPointsPaymentCoverage = 'none' | 'partial' | 'full';

export default function getLoyaltyPointsPaymentCoverage({
  payments,
  payment_plans: paymentPlans,
}: OrderPaymentsInfo): LoyaltyPointsPaymentCoverage {
  // Ignore refunds and failed attempts, but retain pending sales when checking full coverage.
  const sales = payments?.filter((payment) => payment.intent === 'sale' && payment.status !== 'failed') ?? [];
  const confirmedPayments = sales.filter(
    (payment) =>
      payment.status === 'completed' &&
      payment.success &&
      Number.isFinite(Number(payment.amount)) &&
      Number(payment.amount) > 0,
  );

  if (!confirmedPayments.some((payment) => payment.type === LOYALTY_POINTS_PAYMENT_TYPE)) {
    return 'none';
  }

  if (confirmedPayments.some((payment) => creditRefundablePaymentTypes.has(payment.type))) {
    return 'partial';
  }

  // Points plus a discount, benefit or unknown payment type is not necessarily credits + points,
  // nor can we describe that purchase as paid entirely with points.
  if (confirmedPayments.some((payment) => payment.type !== LOYALTY_POINTS_PAYMENT_TYPE)) {
    return 'none';
  }

  const hasUnconfirmedPayments = sales.some(
    (payment) =>
      !Number.isFinite(Number(payment.amount)) ||
      Number(payment.amount) < 0 ||
      (Number(payment.amount) > 0 && (payment.status !== 'completed' || !payment.success)),
  );
  const hasUnfinishedPlan = paymentPlans?.plans.some(
    (plan) =>
      !Number.isFinite(Number(plan.amount)) ||
      Number(plan.amount) < 0 ||
      (Number(plan.amount) > 0 && plan.status !== 'completed' && plan.status !== 'failed'),
  );
  const hasUncertainBalance = sales.some((payment) => {
    // Legacy deferred flows need their own settlement rules; retain existing options.
    if (payment.depositDetails || payment.instalmentDetails || payment.deferredPaymentDetails) {
      return true;
    }
    return payment.paymentScheduleDetails != null && payment.paymentScheduleDetails.balance_amount !== 0;
  });

  // order.total excludes failed/cancelled items and changes after amendments. Classify
  // payment history without comparing or summing it against that mutable value.
  return hasUnconfirmedPayments || hasUnfinishedPlan || hasUncertainBalance ? 'none' : 'full';
}
